import { FC, useDeferredValue, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Row, Col, Button, Modal, Space, Divider, Cascader } from 'antd';
import { AttachmentUpload, FormContent, Radio, Select } from '@maxtropy/components';
import { commonPhoneRegex } from '../../../utils/regex';
import ModalSelect from '../components/ModalSelect';

import { CreateTenantBaseInforequest, TenantBaseInfoResponse } from '../../../api/cc-tenant';
import { EmailPortOptions, PushEmail, TabsBaseProps } from '../util';

import { useCountDownGotoModal } from '../../../utils/help';

import styles from './index.module.scss';
import { areaCodeToName, getParents, useAreaJson } from '../../../utils/hooks';
import { LocationTipsResponse, fetchLocationTips } from '../../../api/cc-location';
import { SearchLocation } from '../../../components/FormComponent';
import { apiV2TenantSendEmailPost, apiV2TenantUpdatePost } from '@maxtropy/tody-mgmt-apis-v2';
import BaseConfig from '../components/BaseConfig';

const formLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
};

const { useForm } = Form;

interface EditTenantBaseInfoProps extends TabsBaseProps {
  tenant?: TenantBaseInfoResponse;
  disabled?: boolean;
}

const EditTenantBaseInfo: FC<EditTenantBaseInfoProps> = props => {
  const [modalApi, modalContextHolder] = Modal.useModal();
  const { onBreacrumbChange, tenant, disabled = false } = props;
  const navigate = useNavigate();
  const [form] = useForm();

  const [locationSearchData, setLocationSearchData] = useState<LocationTipsResponse['tips']>([]);
  const [testSendStatus, setTestSendStatus] = useState<boolean>(tenant?.testSendStatus ?? false);

  const adCode = Form.useWatch<Array<string> | undefined>('adCode', form);

  const [search, setSearch] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const deferredSearch = useDeferredValue(search);

  const [testDisabled, setTestDisabled] = useState<boolean>(true);

  const smtpAddress = Form.useWatch('smtpAddress', form);
  const smtpPort = Form.useWatch('smtpPort', form);
  const sendEmail = Form.useWatch('sendEmail', form);
  const emailPassword = Form.useWatch('emailPassword', form);
  const nameofEmailSender = Form.useWatch('nameofEmailSender', form);
  const testSendEmail = Form.useWatch('testSendEmail', form);
  const isConfigEmail = Form.useWatch('isConfigEmail', form);

  const [testLoading, setTestLoading] = useState<boolean>(false);

  useEffect(() => {
    if (deferredSearch && adCode) {
      setSearchLoading(true);
      fetchLocationTips({
        keywords: deferredSearch,
        city: adCode[2],
      })
        .then(res => {
          setLocationSearchData(res.tips);
          setSearch('');
        })
        .finally(() => {
          setSearchLoading(false);
        });
    }
  }, [adCode, deferredSearch]);

  const districtData = useAreaJson();

  const routes = useMemo(() => {
    return [
      { name: '租户管理' },
      { name: '租户组织权限和账户' },
      { name: `${disabled ? '查看' : '编辑'}租户` },
      { name: '租户基础信息' },
    ];
  }, [disabled]);

  const { setOption, contextHolder } = useCountDownGotoModal(10);

  useEffect(() => {
    if (tenant && districtData) {
      form.setFieldsValue({
        ...tenant,
        isConfigEmail: tenant?.isConfigEmail ? PushEmail.YES : PushEmail.NO,
        adCode: getParents(districtData, tenant.adCode).map(i => i.adcode),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, districtData]);

  useEffect(() => {
    onBreacrumbChange && onBreacrumbChange(routes);
  }, [onBreacrumbChange, routes]);

  const onFinish = (values: Omit<CreateTenantBaseInforequest, 'adCode'> & { adCode: string[] }) => {
    apiV2TenantUpdatePost({
      id: tenant?.id!,
      ...values,
      adCode: values?.adCode?.[values.adCode.length - 1],
      provinceCityDistrict: values?.adCode
        ? areaCodeToName(districtData ?? [], values.adCode[values.adCode.length - 1])
        : undefined,
      affiliatendMcids: (values?.enterprises ?? []).map(i => i.mcid),
      isConfigEmail: Boolean(values.isConfigEmail ?? PushEmail.NO),
      testSendStatus,
    }).then(res => {
      setOption({
        // title: '操作成功',
        okText: '返回列表',
        content: `已经成功编辑租户${res.name}信息`,
        keyboard: false,
        onOk() {
          navigate('/tenant/customer/manage');
        },
      });
    });
  };

  const onCacel = () => {
    modalApi.confirm({
      title: null,
      icon: null,
      content: <div>是否放弃所有未保存信息并返回列表？</div>,
      onOk: () => {
        navigate('/tenant/customer/manage');
      },
    });
  };

  // 在组件中添加 useEffect 监听邮箱相关字段
  const emailFields = ['smtpAddress', 'smtpPort', 'sendEmail', 'emailPassword', 'nameofEmailSender', 'testSendEmail'];

  // 监听邮箱相关字段变化
  useEffect(() => {
    const values = form.getFieldsValue(emailFields);
    const hasChanged = emailFields.some(field => {
      const initialValue = tenant ? tenant[field as keyof TenantBaseInfoResponse] : undefined;
      return values[field] !== initialValue;
    });

    if (hasChanged) {
      setTestSendStatus(false);
    }
  }, [form, tenant, isConfigEmail, smtpAddress, smtpPort, sendEmail, emailPassword, nameofEmailSender, testSendEmail]);

  useEffect(() => {
    const shouldEnableTest =
      isConfigEmail === PushEmail.YES &&
      smtpAddress &&
      smtpPort &&
      sendEmail &&
      emailPassword &&
      nameofEmailSender &&
      testSendEmail;

    setTestDisabled(!shouldEnableTest);
  }, [isConfigEmail, smtpAddress, smtpPort, sendEmail, emailPassword, nameofEmailSender, testSendEmail]);

  const testEmail = () => {
    form.validateFields(emailFields).then(() => {
      const { smtpAddress, smtpPort, sendEmail, emailPassword, nameofEmailSender, testSendEmail } =
        form.getFieldsValue();
      setTestLoading(true);
      apiV2TenantSendEmailPost({
        smtpAddress,
        smtpPort,
        sendEmail,
        emailPassword,
        nameofEmailSender,
        testSendEmail,
      })
        .then(res => {
          if (res.value) {
            setTestSendStatus(true);
            modalApi.success({ content: '邮件推送配置正确，邮件可正常发送' });
          } else {
            setTestSendStatus(false);
            modalApi.error({ content: '邮件推送配置错误，邮件无法发送，请检查相关配置后进行测试；' });
          }
        })
        .finally(() => {
          setTestLoading(false);
        });
    });
  };

  return (
    <div className={styles.container}>
      <Form layout="vertical" {...formLayout} onFinish={onFinish} form={form}>
        <Row>
          <Col span={8}>
            <Form.Item
              name="name"
              label="租户全称"
              rules={[
                { required: true, message: '请输入租户全称' },
                { pattern: /^[\u4e00-\u9fa5a-zA-Z0-9\s\-.,()\uFF08\uFF09]+$/g, message: '租户全称不能包含特殊字符' },
                { max: 30, message: '租户全称最多30个字符' },
              ]}
              validateFirst={true}
            >
              <Input placeholder="请输入租户全称" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="uscc"
              label="统一社会信用代码"
              rules={[
                {
                  pattern: /(^\d{15,15}$)|(^[A-Z0-9]{18,18}$)/,
                  message: '请输入正确的统一社会信用代码',
                },
              ]}
              validateFirst={true}
            >
              <Input placeholder="请输入统一社会信用代码" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="所属省市" name="adCode">
              <Cascader
                disabled={disabled}
                options={districtData}
                fieldNames={{ label: 'name', value: 'adcode', children: 'districts' }}
                placeholder="请选择"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="详细地址" name="address">
              <SearchLocation
                disabled={disabled}
                onSearch={setSearch}
                placeholder="请输入详细地址"
                loading={searchLoading}
                options={(locationSearchData ?? []).map(d => ({
                  value: d.name,
                  label: d.name,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="公司电话"
              name="phone"
              rules={[
                {
                  pattern: commonPhoneRegex,
                  message: '请输入正确的公司电话',
                },
              ]}
            >
              <Input placeholder="请输入公司电话" disabled={disabled} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="营业执照" name="businessLicense">
              <AttachmentUpload
                hoverShow
                disabled={disabled}
                showUploadList={{
                  showDownloadIcon: true,
                }}
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item>
              <BaseConfig disabled={disabled} tenant={tenant} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="添加关联企业" name="enterprises">
              <ModalSelect disabled={disabled} tenant={tenant} />
            </Form.Item>
          </Col>
        </Row>
        <FormContent title="邮箱推送服务配置" style={{ padding: 0 }}>
          <Row>
            <Col span={8}>
              <Form.Item name="isConfigEmail" label="是否配置邮箱推送服务">
                <Radio.Group disabled={disabled}>
                  <Radio value={PushEmail.NO}>否</Radio>
                  <Radio value={PushEmail.YES}>是</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Form.Item noStyle dependencies={['isConfigEmail']}>
              {({ getFieldValue }) => {
                const pushEmail = getFieldValue('isConfigEmail');
                if (pushEmail === PushEmail.YES)
                  return (
                    <>
                      <Col span={8}>
                        <Form.Item
                          name="smtpAddress"
                          label="SMTP服务器地址"
                          rules={[{ required: true, message: '请输入SMTP服务器地址' }]}
                        >
                          <Input placeholder="请输入" disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="smtpPort"
                          label="SMTP端口"
                          rules={[{ required: true, message: '请输入SMTP端口' }]}
                        >
                          <Select placeholder="请选择" options={EmailPortOptions} disabled={disabled}></Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="sendEmail"
                          label="发件邮箱账号"
                          rules={[
                            { required: true, message: '请输入发件邮箱账号' },
                            { type: 'email', message: '请输入正确的邮箱账号' },
                          ]}
                        >
                          <Input placeholder="请输入" disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="emailPassword"
                          label="账号密码"
                          rules={[{ required: true, message: '请输入账号密码' }]}
                        >
                          <Input.Password placeholder="请输入" autoComplete="new-password" disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          name="nameofEmailSender"
                          label="发件人名称显示"
                          rules={[
                            { required: true, message: '请输入发件人名称显示' },
                            { max: 100, message: '不超过100个字' },
                          ]}
                        >
                          <Input placeholder="请输入" disabled={disabled} />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item label="测试邮箱账号" required>
                          <Row>
                            <Form.Item
                              name="testSendEmail"
                              style={{ flex: 1 }}
                              rules={[
                                { required: true, message: '请输入测试邮箱账号' },
                                { type: 'email', message: '请输入正确的邮箱账号' },
                              ]}
                            >
                              <Input placeholder="请输入" disabled={disabled} />
                            </Form.Item>

                            <Button
                              type="link"
                              disabled={disabled || testDisabled || testLoading}
                              onClick={testEmail}
                              style={{ paddingLeft: 8 }}
                            >
                              测试
                            </Button>
                          </Row>
                        </Form.Item>
                      </Col>
                    </>
                  );
              }}
            </Form.Item>
          </Row>
        </FormContent>
        {!disabled && (
          <Row className={styles.buttonWrapper}>
            <Divider />
            <Col span={7}>
              <Form.Item>
                <Space>
                  <Button htmlType="submit" type="primary">
                    保存
                  </Button>
                  <Button onClick={onCacel}>关闭</Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
      {contextHolder}
      {modalContextHolder}
    </div>
  );
};

export default EditTenantBaseInfo;
